import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import BadgeUserType from 'src/components/Badge/BadgeUserType';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import logger from 'src/util/logger';

import AdminUserControls from '../AdminUserControls';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_user.admin_user_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more users. Please try again.',
  },
});

const AdminUserListFragment = graphql`
  fragment AdminUserList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminUserListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    users(
      first: $count
      after: $cursor
    ) @connection(key: "AdminUserList_users") {
      edges {
        cursor
        node {
          uuid
          externalIdentifier
          type
          email
          givenName
          familyName
          active {
            start
            finish
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows rowCount={3} />;
}

/**
 * Admin list of users with load-more pagination. Field errors throw to the
 * route's PlaceholderErrorBoundary; load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactElement} AdminUserList component.
 */
function AdminUserList({ viewer: viewerRef = null }) {
  const {
    data: viewer, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminUserListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  const intl = useIntl();

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminUserList: the route query returned no viewer');
  }

  const { users } = viewer;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.page_title.label', defaultMessage: 'Enosi Admin - Users' });
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.breadcrumb.admin.label', defaultMessage: 'Admin' });
  const breadcrumbUsers = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.breadcrumb.users.label', defaultMessage: 'Users' });
  const headingUsers = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.heading.label', defaultMessage: 'Users' });

  // Table headers
  const headerStatus = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.status.label', defaultMessage: 'Status' });
  const headerType = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.type.label', defaultMessage: 'Type' });
  const headerUsername = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.username.label', defaultMessage: 'Username' });
  const headerEmail = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.email.label', defaultMessage: 'Email' });
  const headerExternalId = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.external_identifier.label', defaultMessage: 'Ext ID' });
  const headerExternalIdTitle = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.external_identifier.title', defaultMessage: 'External identifier' });

  // Load more button
  const loadMoreStateLoadMore = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const loadMoreStateNoMore = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.load_more_button.state_no_more.label', defaultMessage: 'No more users to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbUsers },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{headingUsers}</h1>
        <div className="float-end mb-4">
          <AdminUserControls />
        </div>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{headerStatus}</th>
                <th scope="col">{headerType}</th>
                <th scope="col">{headerUsername}</th>
                <th scope="col">{headerEmail}</th>
                <th scope="col">
                  <abbr title={headerExternalIdTitle}>{headerExternalId}</abbr>
                </th>
              </tr>
            </thead>
            <tbody>
              {
                users && users.edges && users.edges.map((edge) => {
                  const { node: user } = edge;
                  const {
                    uuid, externalIdentifier, type, email, active,
                  } = user;

                  return (
                    <tr key={`admin-user-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <td>
                        <BadgeUserType type={type} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/users/${uuid}`}>
                          <div>{displayUserName(intl, user)}</div>
                          <div>{email}</div>
                        </Link>
                        <div>{`(${externalIdentifier})`}</div>
                      </th>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={loadMoreStateLoadMore}
            copyNoMore={loadMoreStateNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminUserList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminUserList;
